import React, { useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { NEARBY_SUBURBS, getNearbyBySlug } from "./nearby";
import JsonLd from "./JsonLd";

// Meta setter — mirrors BlogPost pattern (no react-helmet dep).
const useDocumentMeta = ({ title, description, canonicalPath, image }) => {
  useEffect(() => {
    if (title) document.title = title;
    const setMeta = (selector, attrs) => {
      let el = document.querySelector(selector);
      if (!el) {
        el = document.createElement(attrs.tag);
        Object.entries(attrs.props).forEach(([k, v]) => el.setAttribute(k, v));
        document.head.appendChild(el);
      }
      if (attrs.content !== undefined) el.setAttribute("content", attrs.content);
      if (attrs.href !== undefined) el.setAttribute("href", attrs.href);
    };
    if (description) {
      setMeta('meta[name="description"]', { tag: "meta", props: { name: "description" }, content: description });
      setMeta('meta[property="og:description"]', { tag: "meta", props: { property: "og:description" }, content: description });
    }
    if (title) {
      setMeta('meta[property="og:title"]', { tag: "meta", props: { property: "og:title" }, content: title });
    }
    if (image) {
      setMeta('meta[property="og:image"]', { tag: "meta", props: { property: "og:image" }, content: image });
    }
    if (canonicalPath) {
      const href = `https://passionpilates.au${canonicalPath}`;
      setMeta('link[rel="canonical"]', { tag: "link", props: { rel: "canonical" }, href });
      setMeta('meta[property="og:url"]', { tag: "meta", props: { property: "og:url" }, content: href });
    }
  }, [title, description, canonicalPath, image]);
};

export default function NearbySuburb() {
  const { slug } = useParams();
  const entry = getNearbyBySlug(slug);

  const title = entry
    ? `Reformer Pilates near ${entry.suburb} — ${entry.driveMinutes} min from Passion Pilates Chadstone`
    : "Reformer Pilates near you — Passion Pilates Chadstone";
  const description = entry
    ? `Passion Pilates is the closest boutique reformer studio to ${entry.suburb} (${entry.postcode}) — ${entry.distanceKm}km away, ${entry.driveMinutes}-min drive. 9-bed studio, five class styles, memberships from $35/week.`
    : "Passion Pilates — Chadstone's boutique reformer studio serving Melbourne's inner south-east.";

  useDocumentMeta({
    title,
    description,
    canonicalPath: entry ? `/pilates-near/${entry.slug}` : "/pilates-near",
    image: "https://customer-assets.emergentagent.com/job_passion-flow/artifacts/zu9iz4h2_77553E99-750E-4A70-8BB1-6AB3D32D3BCB.png",
  });

  if (!entry) return <Navigate to="/pilates-near" replace />;

  // JSON-LD LocalBusiness + Service schema targeting the specific suburb.
  // AI answer engines pull this to answer "best pilates near <suburb>" queries.
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": ["LocalBusiness", "SportsActivityLocation", "HealthClub"],
    "@id": `https://passionpilates.au/pilates-near/${entry.slug}`,
    name: `Passion Pilates — Reformer Pilates near ${entry.suburb}`,
    description,
    url: `https://passionpilates.au/pilates-near/${entry.slug}`,
    telephone: "+61498451148",
    email: "passion.chadstone@gmail.com",
    priceRange: "$$",
    address: {
      "@type": "PostalAddress",
      streetAddress: "5/49 Batesford Road",
      addressLocality: "Chadstone",
      addressRegion: "VIC",
      postalCode: "3148",
      addressCountry: "AU",
    },
    geo: {
      "@type": "GeoCoordinates",
      latitude: -37.8875,
      longitude: 145.0876,
    },
    areaServed: {
      "@type": "GeoCircle",
      geoMidpoint: {
        "@type": "GeoCoordinates",
        latitude: -37.8875,
        longitude: 145.0876,
      },
      geoRadius: "15000",
    },
    openingHoursSpecification: {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
      opens: "06:00",
      closes: "20:30",
    },
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: "4.9",
      reviewCount: "87",
      bestRating: "5",
      worstRating: "1",
    },
  };

  const related = NEARBY_SUBURBS.filter((s) => s.slug !== entry.slug).slice(0, 6);

  return (
    <div
      className="min-h-screen bg-[#0a0a0a] text-[#E8DCC4]"
      style={{ fontFamily: "Georgia, 'Times New Roman', serif" }}
    >
      <JsonLd data={jsonLd} />

      {/* Header */}
      <header className="border-b border-[#E8DCC4]/15">
        <div className="max-w-4xl mx-auto px-6 lg:px-10 py-8 flex items-center justify-between">
          <Link
            to="/pilates-near"
            data-testid="nearby-back-link"
            className="font-sans text-[11px] tracking-[0.3em] uppercase font-semibold text-[#C97654] hover:text-[#E8DCC4] transition-colors"
          >
            ← All suburbs
          </Link>
          <Link to="/" className="font-sans text-[11px] tracking-[0.3em] uppercase text-[#E8DCC4]/60 hover:text-[#C97654] transition-colors">
            Passion Pilates
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-4xl mx-auto px-6 lg:px-10 pt-14 pb-4">
        <p className="font-sans text-[11px] tracking-[0.3em] uppercase text-[#C97654] mb-5">
          {entry.distanceKm} km · {entry.driveMinutes} min drive from Chadstone
        </p>
        <h1 className="text-[#FBF6EC] text-[38px] md:text-[56px] leading-[1.02] tracking-[-1px] font-normal mb-6">
          Reformer Pilates near <em className="italic text-[#C97654]">{entry.suburb}.</em>
        </h1>
        <p className="font-sans text-[17px] md:text-[19px] leading-[1.65] text-[#E8DCC4]/85 max-w-2xl">
          {entry.intro}
        </p>

        <div className="mt-10 flex flex-wrap gap-3">
          <a
            href="https://clients.mindbodyonline.com/classic/ws?studioid=5751621&stype=41&sTG=23&prodId=100052"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nearby-cta-trial"
            className="inline-block font-sans font-bold text-[13px] tracking-[0.25em] uppercase bg-[#8B2635] hover:bg-[#a03042] text-[#FBF6EC] px-8 py-4 rounded-sm transition-colors duration-300"
          >
            2 weeks · $55 →
          </a>
          <a
            href="https://go.mindbodyonline.com/book/widgets/schedules/view/24485199377/schedule"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nearby-cta-schedule"
            className="inline-block font-sans font-bold text-[13px] tracking-[0.25em] uppercase border border-[#E8DCC4]/40 text-[#E8DCC4] hover:bg-[#E8DCC4] hover:text-[#0a0a0a] px-8 py-4 rounded-sm transition-colors duration-300"
          >
            View schedule
          </a>
        </div>
      </section>

      {/* Why us for this suburb */}
      <section className="max-w-4xl mx-auto px-6 lg:px-10 py-14 border-t border-[#E8DCC4]/15 mt-10">
        <p className="font-sans text-[11px] tracking-[0.3em] uppercase font-semibold text-[#C97654] mb-5">
          Why {entry.suburb} chooses Passion
        </p>
        <ul className="space-y-4">
          {entry.localHooks.map((hook) => (
            <li key={hook} className="flex items-start gap-4 font-sans text-[17px] leading-[1.6] text-[#E8DCC4]/85">
              <span className="text-[#C97654] font-bold mt-[6px] w-2 h-2 bg-[#C97654] rounded-full flex-shrink-0"></span>
              <span>{hook}</span>
            </li>
          ))}
        </ul>
      </section>

      {/* Studio quick facts */}
      <section className="max-w-4xl mx-auto px-6 lg:px-10 py-14 border-t border-[#E8DCC4]/15">
        <div className="grid md:grid-cols-2 gap-10">
          <div>
            <p className="font-sans text-[11px] tracking-[0.3em] uppercase font-semibold text-[#C97654] mb-3">Address</p>
            <p className="text-[#FBF6EC] text-[20px] leading-tight">5/49 Batesford Road<br />Chadstone VIC 3148</p>
          </div>
          <div>
            <p className="font-sans text-[11px] tracking-[0.3em] uppercase font-semibold text-[#C97654] mb-3">Hours</p>
            <p className="text-[#FBF6EC] text-[20px] leading-tight">6am – 8:30pm<br />7 days a week</p>
          </div>
          <div>
            <p className="font-sans text-[11px] tracking-[0.3em] uppercase font-semibold text-[#C97654] mb-3">Phone</p>
            <a href="tel:+61498451148" className="text-[#FBF6EC] text-[20px] leading-tight hover:text-[#C97654] transition-colors">0498 451 148</a>
          </div>
          <div>
            <p className="font-sans text-[11px] tracking-[0.3em] uppercase font-semibold text-[#C97654] mb-3">Distance from {entry.suburb}</p>
            <p className="text-[#FBF6EC] text-[20px] leading-tight">{entry.distanceKm} km<br />{entry.driveMinutes}-min drive</p>
          </div>
        </div>
      </section>

      {/* Nearby landmarks — local relevance signal */}
      <section className="max-w-4xl mx-auto px-6 lg:px-10 py-14 border-t border-[#E8DCC4]/15">
        <p className="font-sans text-[11px] tracking-[0.3em] uppercase font-semibold text-[#C97654] mb-5">
          Near {entry.suburb}
        </p>
        <p className="font-sans text-[16px] leading-[1.7] text-[#E8DCC4]/80 max-w-2xl">
          Passion Pilates is easily reached from {entry.landmarks.slice(0, -1).join(", ")} and {entry.landmarks[entry.landmarks.length - 1]}.
          Closest train stations include {entry.nearbyStations.join(" and ")}. Free on-site parking makes drop-off simple for members and Mums & Bubs classes.
        </p>
      </section>

      {/* CTA */}
      <section className="max-w-4xl mx-auto px-6 lg:px-10 py-14 border-t border-[#E8DCC4]/15">
        <p className="font-sans text-[11px] tracking-[0.3em] uppercase font-semibold text-[#C97654] mb-3">
          Start this week
        </p>
        <h3 className="text-[#FBF6EC] text-[28px] md:text-[36px] leading-tight font-normal tracking-[-0.5px] mb-6">
          Try 6 reformer classes for <em className="italic text-[#C97654]">$55.</em>
        </h3>
        <a
          href="https://clients.mindbodyonline.com/classic/ws?studioid=5751621&stype=41&sTG=23&prodId=100052"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-block font-sans font-bold text-[13px] tracking-[0.25em] uppercase bg-[#8B2635] hover:bg-[#a03042] text-[#FBF6EC] px-8 py-4 rounded-sm transition-colors duration-300"
        >
          Claim the trial →
        </a>
      </section>

      {/* Related suburbs */}
      {related.length > 0 && (
        <section className="border-t border-[#E8DCC4]/15 py-14">
          <div className="max-w-4xl mx-auto px-6 lg:px-10">
            <p className="font-sans text-[11px] tracking-[0.3em] uppercase font-semibold text-[#C97654] mb-6">
              Also serving
            </p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
              {related.map((s) => (
                <Link
                  key={s.slug}
                  to={`/pilates-near/${s.slug}`}
                  className="group block border border-[#E8DCC4]/15 hover:border-[#C97654] p-5 transition-colors"
                >
                  <p className="font-sans text-[11px] tracking-[0.25em] uppercase text-[#C97654] mb-2">
                    {s.driveMinutes} min drive
                  </p>
                  <h4 className="text-[#FBF6EC] text-[18px] leading-tight font-normal tracking-[-0.2px] group-hover:text-[#C97654] transition-colors">
                    Pilates near {s.suburb}
                  </h4>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Footer */}
      <footer className="border-t border-[#E8DCC4]/15 py-10">
        <div className="max-w-4xl mx-auto px-6 lg:px-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <p className="font-sans text-[12px] tracking-[0.2em] uppercase text-[#E8DCC4]/55">Passion Pilates · Chadstone</p>
          <Link to="/" className="font-sans text-[12px] tracking-[0.2em] uppercase font-bold text-[#E8DCC4] border-b-2 border-[#C97654] pb-[2px] hover:text-[#C97654] transition-colors">
            Back to home →
          </Link>
        </div>
      </footer>
    </div>
  );
}
